function trimTrailingSlashes(value: string): string {
  return value.replace(/\/+$/, "");
}

function isLocalHostName(value: string): boolean {
  return value === "localhost" || value === "127.0.0.1" || value === "0.0.0.0";
}

function hasProtocol(value: string): boolean {
  return /^[a-z][a-z\d+\-.]*:\/\//i.test(value);
}

function getHostName(value: string): string {
  const host = value.split(/[/?#]/, 1)[0].replace(/^\[/, "").replace(/\]$/, "");
  return host.split(":", 1)[0].toLowerCase();
}

function isHostLikeValue(value: string): boolean {
  if (value.startsWith("/") || value.includes(" ")) {
    return false;
  }

  const host = value.split(/[/?#]/, 1)[0];
  const hostname = getHostName(value);
  const hasPort = /:\d+$/.test(host);

  return Boolean(hostname) && (isLocalHostName(hostname) || hostname.includes(".") || hasPort);
}

function addDefaultProtocol(value: string): string {
  if (hasProtocol(value) || !isHostLikeValue(value)) {
    return value;
  }

  return `${isLocalHostName(getHostName(value)) ? "http" : "https"}://${value}`;
}

function normalizeVcPath(pathname: string): string {
  const value = trimTrailingSlashes(pathname);
  const path = value.startsWith("/") ? value : `/${value}`;
  const lower = path.toLowerCase();
  const apiVcsIndex = lower.indexOf("/api/vcs");

  if (apiVcsIndex >= 0) {
    return path.substring(0, apiVcsIndex + "/api/vcs".length);
  }

  if (!path || path === "/" || lower === "/api" || lower === "/vcs") {
    return "/api/vcs";
  }

  if (lower.endsWith("/api")) {
    return `${path}/vcs`;
  }

  return path;
}

function normalizeVcServerRoot(value: string): string {
  const trimmed = trimTrailingSlashes(value.trim());
  if (!trimmed) {
    return "";
  }

  try {
    const parsed = new URL(addDefaultProtocol(trimmed));
    parsed.pathname = normalizeVcPath(parsed.pathname);
    parsed.search = "";
    parsed.hash = "";
    return trimTrailingSlashes(parsed.toString());
  } catch {
    return normalizeVcPath(trimmed);
  }
}

export function getVerifiedCredentialsBaseUrl(settingsVcServer?: string, settingsFabrixServer?: string): string {
  const directVcServer = (process.env.REACT_APP_VC_SERVER || "").trim();
  const configuredVcServer = (settingsVcServer || "").trim();
  const configuredFabrixServer = (settingsFabrixServer || "").trim();
  const isProd = process.env.NODE_ENV === "production";

  if (isProd) {
    if (directVcServer) {
      return normalizeVcServerRoot(directVcServer);
    }
    if (configuredVcServer && configuredVcServer !== "/api/vcs") {
      return normalizeVcServerRoot(configuredVcServer);
    }
    if (configuredFabrixServer) {
      return normalizeVcServerRoot(configuredFabrixServer);
    }
    return "/api/vcs";
  }

  if (directVcServer) {
    return normalizeVcServerRoot(directVcServer);
  }

  if (configuredVcServer && configuredVcServer !== "/api/vcs") {
    return normalizeVcServerRoot(configuredVcServer);
  }

  if (configuredFabrixServer) {
    return normalizeVcServerRoot(configuredFabrixServer);
  }

  const fabrixServer = (process.env.REACT_APP_FABRIX_SERVER || "").trim();
  if (fabrixServer) {
    return normalizeVcServerRoot(fabrixServer);
  }

  return "http://localhost:4530/api/vcs";
}

export function getNormalizedVerifiedCredentialsBaseUrl(settingsVcServer?: string, settingsFabrixServer?: string): string {
  return trimTrailingSlashes(getVerifiedCredentialsBaseUrl(settingsVcServer, settingsFabrixServer));
}

export function getVerifiedCredentialsBaseUrlCandidates(settingsVcServer?: string, settingsFabrixServer?: string): string[] {
  const candidates: string[] = [];

  const addCandidate = (value?: string) => {
    const normalized = normalizeVcServerRoot(String(value ?? ""));
    if (!normalized) {
      return;
    }
    if (!candidates.includes(normalized)) {
      candidates.push(normalized);
    }
  };

  const addRawCandidate = (value?: string) => {
    const normalized = trimTrailingSlashes(String(value ?? "").trim());
    if (!normalized) {
      return;
    }
    if (!candidates.includes(normalized)) {
      candidates.push(normalized);
    }
  };

  addCandidate(settingsVcServer);
  addCandidate(process.env.REACT_APP_VC_SERVER);
  addCandidate(getVerifiedCredentialsBaseUrl(settingsVcServer, settingsFabrixServer));

  const snapshot = [...candidates];
  for (const candidate of snapshot) {
    const lower = candidate.toLowerCase();
    if (lower.endsWith("/api/vcs")) {
      const root = candidate.slice(0, candidate.length - "/api/vcs".length);
      addRawCandidate(`${root}/api`);
      addRawCandidate(`${root}/vcs`);
    } else if (lower.endsWith("/vcs")) {
      const root = candidate.slice(0, candidate.length - "/vcs".length);
      addRawCandidate(`${root}/api/vcs`);
      addRawCandidate(`${root}/api`);
    }
  }

  addCandidate("/api/vcs");

  return candidates;
}
